<template>
  <div class="evidence" data-testid="evidence">
    <!-- Upload Section -->
    <div 
      v-if="!viewMode" 
      class="evidence__upload-section"
      data-testid="evidence__upload-section"
    >
      <!-- Header with constraints info -->
      <div class="evidence__header d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <v-icon small class="mr-2" color="primary">mdi-paperclip</v-icon>
          <span class="text-subtitle-2 font-weight-medium">File Attachments</span>
        </div>
        <div class="evidence__constraints-chip">
          <v-icon x-small class="mr-1" color="grey darken-1">mdi-information-outline</v-icon>
          <span>Max {{ maxFiles }} files, {{ formatFileSize(maxTotalSize) }} per upload</span>
        </div>
      </div>

      <!-- Drag & Drop Zone -->
      <div
        class="evidence__dropzone"
        :class="{ 
          'evidence__dropzone--active': isDragOver,
          'evidence__dropzone--disabled': isLoading 
        }"
        @dragover.prevent="handleDragOver"
        @dragleave.prevent="handleDragLeave"
        @drop.prevent="handleDrop"
        data-testid="evidence__dropzone"
      >
        <div class="evidence__dropzone-content text-center">
          <v-icon 
            :size="48" 
            :color="isDragOver ? 'primary' : 'grey lighten-1'"
            class="mb-3"
          >
            mdi-cloud-upload-outline
          </v-icon>
          <p class="text-body-2 grey--text text--darken-1 mb-2">
            Drag & drop files here
          </p>
          <p class="text-caption grey--text mb-3">or</p>
          <v-btn
            color="primary"
            outlined
            small
            :disabled="isLoading"
            @click="triggerFileInput"
            data-testid="evidence__browse-btn"
          >
            <v-icon left small>mdi-folder-open-outline</v-icon>
            Browse Files
          </v-btn>
          <input
            ref="fileInput"
            type="file"
            multiple
            hidden
            :accept="acceptedFileTypes"
            @change="handleFileSelect"
            data-testid="evidence__file-input"
          />
        </div>
        <p class="text-caption grey--text text-center mt-3">
          Supported: {{ supportedFormatsDisplay }}
        </p>
      </div>

      <!-- Selected Files Preview -->
      <div 
        v-if="selectedFiles.length > 0" 
        class="evidence__selected-files mt-4"
        data-testid="evidence__selected-files"
      >
        <div class="d-flex align-center justify-space-between mb-2">
          <div class="d-flex align-center">
            <span class="text-body-2 font-weight-medium">
              Selected Files ({{ selectedFiles.length }}/{{ maxFiles }})
            </span>
            <!-- Total size display -->
            <span class="text-caption grey--text ml-3">
              Total: {{ formatFileSize(totalSelectedSize) }} / {{ formatFileSize(maxTotalSize) }}
            </span>
            <!-- Error indicator when file count or size exceeds limit -->
            <v-icon 
              v-if="selectedFiles.length > maxFiles || totalSelectedSize > maxTotalSize" 
              small 
              color="error" 
              class="ml-2"
            >
              mdi-alert-circle
            </v-icon>
          </div>
          <v-btn
            text
            x-small
            color="error"
            @click="clearAllFiles"
            data-testid="evidence__clear-all-btn"
          >
            <v-icon x-small left>mdi-close-circle</v-icon>
            Clear All
          </v-btn>
        </div>
        
        <div class="evidence__file-list">
          <v-chip
            v-for="(file, idx) in selectedFiles"
            :key="`selected-${idx}`"
            close
            class="ma-1 evidence__file-chip"
            :color="getFileChipColor(file)"
            text-color="white"
            @click:close="removeFile(idx)"
            data-testid="evidence__file-chip"
          >
            <v-icon left>{{ getFileIcon(file.name) }}</v-icon>
            <span class="evidence__file-name">{{ truncateFileName(file.name) }}</span>
            <span class="evidence__file-size-chip ml-2">({{ formatFileSize(file.size) }})</span>
          </v-chip>
        </div>

        <!-- File Count Exceeded Warning -->
        <v-alert
          v-if="selectedFiles.length > maxFiles"
          type="error"
          dense
          text
          class="mt-2"
          data-testid="evidence__file-count-error"
        >
          Maximum {{ maxFiles }} files allowed per upload. Please remove {{ selectedFiles.length - maxFiles }} file(s).
        </v-alert>

        <!-- Total Size Warning -->
        <v-alert
          v-if="totalSelectedSize > maxTotalSize"
          type="error"
          dense
          text
          class="mt-2"
          data-testid="evidence__size-error"
        >
          Total file size exceeds {{ formatFileSize(maxTotalSize) }} limit
        </v-alert>

        <!-- Upload Button -->
        <div class="d-flex justify-end mt-3">
          <v-btn
            color="primary"
            :loading="isLoading"
            :disabled="isLoading || selectedFiles.length === 0 || selectedFiles.length > maxFiles || totalSelectedSize > maxTotalSize"
            @click="uploadFiles"
            data-testid="evidence__upload-btn"
          >
            <v-icon left small>mdi-upload</v-icon>
            Upload {{ selectedFiles.length }} file(s)
          </v-btn>
        </div>
      </div>
    </div>

    <!-- Uploaded Files Section - Table View -->
    <div 
      v-if="hasUploadedFiles" 
      class="evidence__uploaded-files"
      :class="{ 'mt-4': !viewMode }"
      data-testid="evidence__uploaded-files"
    >
      <div class="d-flex align-center justify-space-between mb-3">
        <div class="d-flex align-center">
          <v-icon small class="mr-2" color="success">mdi-check-circle</v-icon>
          <span class="text-body-2 font-weight-medium">Uploaded Files ({{ uploadedFilesCount }})</span>
        </div>
        <!-- Download All Button -->
        <v-tooltip v-if="uploadedFilesCount > 1" bottom content-class="tooltip-bottom">
          <template v-slot:activator="{ on, attrs }">
            <v-btn
              small
              outlined
              color="primary"
              v-bind="attrs"
              v-on="on"
              :disabled="isDownloadDisabled"
              @click="downloadAllFiles"
              data-testid="evidence__download-all-btn"
            >
              <v-icon left small>mdi-download-multiple</v-icon>
              Download All
            </v-btn>
          </template>
          <span>{{ isDownloadDisabled ? 'Download not available in edit mode' : 'Download all files' }}</span>
        </v-tooltip>
      </div>
      
      <!-- Table for upload_details -->
      <v-simple-table 
        v-if="uploadDetailsList.length > 0" 
        dense 
        class="evidence__table"
        data-testid="evidence__files-table"
      >
        <template v-slot:default>
          <thead class="tableHeaderBg">
            <tr>
              <th class="text-left">#</th>
              <th class="text-left">Created By</th>
              <th class="text-left">File Name</th>
              <th class="text-left">Type</th>
              <th class="text-right">Size</th>
              <th class="text-left">Uploaded At</th>
              <th class="text-center">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(fileDetail, idx) in uploadDetailsList" 
              :key="`uploaded-row-${fileDetail._id}`"
              data-testid="evidence__file-row"
            >
              <td class="text-left">{{ idx + 1 }}</td>
              <td class="text-left">
                <div 
                  v-if="getUploadUserInfo(fileDetail)" 
                  class="evidence__created-by"
                  data-testid="evidence__created-by"
                >
                  <div class="evidence__created-by-avatar">
                    <img src="/img/icons/User.svg" alt="" class="evidence__avatar-icon" />
                  </div>
                  <div class="evidence__created-by-info">
                    <div class="evidence__created-by-name">
                      {{ getUploadUserInfo(fileDetail).name || getUploadUserInfo(fileDetail).email || '-' }}
                    </div>
                    <div 
                      v-if="getUploadUserInfo(fileDetail).role" 
                      class="evidence__created-by-role"
                    >
                      {{ formatUserRole(getUploadUserInfo(fileDetail).role) }}
                    </div>
                  </div>
                </div>
                <span v-else class="grey--text text-caption">-</span>
              </td>
              <td class="text-left">
                <div class="d-flex align-center">
                  <div class="evidence__file-icon-wrapper" :class="getFileIconClass(fileDetail.uploadname || fileDetail.originalname)">
                    <v-icon small color="white">
                      {{ getFileIcon(fileDetail.uploadname || fileDetail.originalname) }}
                    </v-icon>
                  </div>
                  <span class="evidence__table-filename" :title="fileDetail.uploadname || fileDetail.originalname">
                    {{ fileDetail.uploadname || fileDetail.originalname }}
                  </span>
                </div>
              </td>
              <td class="text-left">
                <div class="evidence__type-badge" :class="getFileTypeClass(fileDetail.uploadname || fileDetail.originalname)">
                  {{ getFileExtension(fileDetail.uploadname || fileDetail.originalname) }}
                </div>
              </td>
              <td class="text-right">
                <span class="evidence__file-size">{{ formatFileSize(fileDetail.size) }}</span>
              </td>
              <td class="text-left">
                <span class="evidence__upload-date">{{ formatUploadDate(fileDetail.created_at) }}</span>
              </td>
              <td class="text-center">
                <v-tooltip bottom content-class="tooltip-bottom">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      :disabled="isDownloadDisabled"
                      @click="downloadFile(fileDetail._id)"
                      data-testid="evidence__download-btn"
                    >
                      <v-icon small :color="isDownloadDisabled ? 'grey darken-1' : 'primary'">mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ isDownloadDisabled ? 'Download not available in edit mode' : 'Download' }}</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
      
      <!-- Fallback table if no upload_details -->
      <v-simple-table 
        v-else 
        dense 
        class="evidence__table"
        data-testid="evidence__files-table-fallback"
      >
        <template v-slot:default>
          <thead class="tableHeaderBg">
            <tr>
              <th class="text-left" style="width: 40px;">#</th>
              <th class="text-left">File</th>
              <th class="text-center" style="width: 80px;">Actions</th>
            </tr>
          </thead>
          <tbody>
            <tr 
              v-for="(uploadId, idx) in uploadedFileIds" 
              :key="`uploaded-fallback-row-${uploadId}`"
              data-testid="evidence__file-row"
            >
              <td class="text-left">{{ idx + 1 }}</td>
              <td class="text-left">
                <div class="d-flex align-center">
                  <v-icon small class="mr-2" color="success">mdi-file-check</v-icon>
                  <span>File {{ idx + 1 }}</span>
                </div>
              </td>
              <td class="text-center">
                <v-tooltip bottom>
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn
                      icon
                      small
                      v-bind="attrs"
                      v-on="on"
                      :disabled="isDownloadDisabled"
                      @click="downloadFile(uploadId)"
                      data-testid="evidence__download-btn"
                    >
                      <v-icon small :color="isDownloadDisabled ? 'grey darken-1' : 'primary'">mdi-download</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ isDownloadDisabled ? 'Download not available in edit mode' : 'Download' }}</span>
                </v-tooltip>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>
    </div>

    <!-- <div 
      v-else-if="legacyUploadId" 
      class="evidence__legacy-upload"
      :class="{ 'mt-4': !viewMode }"
      data-testid="evidence__legacy-upload"
    >
      <v-tooltip bottom>
        <template v-slot:activator="{ on, attrs }">
          <v-btn
            text
            small
            color="primary"
            v-bind="attrs"
            v-on="on"
            :disabled="isDownloadDisabled"
            @click="downloadLegacyFile"
            data-testid="evidence__legacy-download-btn"
          >
            <v-icon left small>mdi-download</v-icon>
            Download Attachment
          </v-btn>
        </template>
        <span>{{ isDownloadDisabled ? 'Download not available in edit mode' : 'Download attachment' }}</span>
      </v-tooltip>
    </div> -->

    <!-- Empty State (View Mode) -->
    <div 
      v-else-if="viewMode" 
      class="evidence__empty text-center py-4"
      data-testid="evidence__empty"
    >
      <v-icon size="48" color="grey lighten-1">mdi-file-hidden</v-icon>
      <p class="text-body-2 grey--text mt-2">No attachments available</p>
    </div>
  </div>
</template>

<script>
/**
 * Evidence Component
 * 
 * Handles file attachments for commentary evidence tab.
 * Supports multiple file upload with drag & drop, file preview,
 * and download functionality.
 * 
 * @component Evidence
 */

// Constants for file upload configuration
const MAX_FILES = 10; // Maximum 10 files per upload
const MAX_TOTAL_SIZE = 25 * 1024 * 1024; // 25MB per upload
const ACCEPTED_EXTENSIONS = '.xlsx,.xls,.xlsb,.csv,.pdf,.doc,.docx,.jpg,.jpeg,.png,.ppt,.pptx';
const SUPPORTED_FORMATS_DISPLAY = 'Excel, CSV, PDF, Word, Images, PowerPoint';

// File icon mapping based on extension
const FILE_ICON_MAP = {
  xlsx: 'mdi-file-excel',
  xls: 'mdi-file-excel',
  xlsb: 'mdi-file-excel',
  csv: 'mdi-file-delimited',
  pdf: 'mdi-file-pdf-box',
  doc: 'mdi-file-word',
  docx: 'mdi-file-word',
  jpg: 'mdi-file-image',
  jpeg: 'mdi-file-image',
  png: 'mdi-file-image',
  ppt: 'mdi-file-powerpoint',
  pptx: 'mdi-file-powerpoint',
};

// File chip color mapping based on extension
const FILE_COLOR_MAP = {
  xlsx: 'green darken-1',
  xls: 'green darken-1',
  xlsb: 'green darken-1',
  csv: 'teal',
  pdf: 'red darken-1',
  doc: 'blue darken-1',
  docx: 'blue darken-1',
  jpg: 'purple',
  jpeg: 'purple',
  png: 'purple',
  ppt: 'orange darken-1',
  pptx: 'orange darken-1',
};

export default {
  name: 'Evidence',
  
  props: {
    /** Commentary ID to attach files to */
    commentaryId: {
      type: String,
      required: true,
    },
    /** Cycle type for API filter */
    cycletype: {
      type: String,
      required: true,
    },
    /** Optional reporting type for Balance Sheet */
    reportingType: {
      type: String,
      default: '',
    },
    /** Array of already uploaded file IDs */
    uploads: {
      type: Array,
      default: () => [],
    },
    /** Array of upload detail objects from API */
    uploadDetails: {
      type: Array,
      default: () => [],
    },
    /** Legacy single upload ID */
    legacyUploadId: {
      type: String,
      default: '',
    },
    /** View-only mode (no upload allowed) */
    viewMode: {
      type: Boolean,
      default: false,
    },
    /** Maximum number of files allowed */
    maxFiles: {
      type: Number,
      default: MAX_FILES,
    },
    /** Maximum total file size in bytes */
    maxTotalSize: {
      type: Number,
      default: MAX_TOTAL_SIZE,
    },
    /** User ID for upload tracking */
    userId: {
      type: String,
      default: '',
    },
    /** User role (preparer, approver, or regional approver) */
    userRole: {
      type: String,
      default: '',
    },
    /** User name for upload tracking */
    userName: {
      type: String,
      default: '',
    },
    /** User email for upload tracking */
    userEmail: {
      type: String,
      default: '',
    },
    /**
     * When true, download buttons are disabled.
     * Used when user doesn't have permission to download files.
     */
    disableDownload: {
      type: Boolean,
      default: false,
    },

    /**
     * Backward-compatible alias for `disableDownload`.
     * Some parent templates use `:disabled-download`.
     */
    disabledDownload: {
      type: Boolean,
      default: false,
    },
  },

  data() {
    return {
      // Selected files pending upload
      selectedFiles: [],
      // Drag state for visual feedback
      isDragOver: false,
      // Upload loading state
      isLoading: false,
      // Upload progress percentage
      uploadPercentage: 0,
      // Accepted file types
      acceptedFileTypes: ACCEPTED_EXTENSIONS,
      // Display text for supported formats
      supportedFormatsDisplay: SUPPORTED_FORMATS_DISPLAY,
    };
  },

  computed: {
    isDownloadDisabled() {
      return Boolean(this.disableDownload || this.disabledDownload);
    },
    /**
     * Get list of uploaded file IDs
     * @returns {Array<string>} Array of upload IDs
     */
    uploadedFileIds() {
      return this.uploads || [];
    },

    /**
     * Get list of upload detail objects sorted by created_at (latest first)
     * @returns {Array<Object>} Array of upload detail objects
     */
    uploadDetailsList() {
      if (!this.uploadDetails || this.uploadDetails.length === 0) {
        return [];
      }
      // Sort by created_at descending (latest first)
      return [...this.uploadDetails].sort((a, b) => {
        const dateA = new Date(a.created_at || 0);
        const dateB = new Date(b.created_at || 0);
        return dateB - dateA;
      });
    },

    /**
     * Check if there are any uploaded files
     * @returns {boolean}
     */
    hasUploadedFiles() {
      return this.uploadDetailsList.length > 0 || this.uploadedFileIds.length > 0;
    },

    /**
     * Get the count of uploaded files
     * @returns {number}
     */
    uploadedFilesCount() {
      return this.uploadDetailsList.length > 0 
        ? this.uploadDetailsList.length 
        : this.uploadedFileIds.length;
    },

    /**
     * Calculate total size of selected files
     * @returns {number} Total size in bytes
     */
    totalSelectedSize() {
      return this.selectedFiles.reduce((total, file) => total + file.size, 0);
    },
  },

  methods: {
    /**
     * Trigger the hidden file input click
     */
    triggerFileInput() {
      if (this.$refs.fileInput) {
        this.$refs.fileInput.click();
      }
    },

    /**
     * Handle file selection from input
     * @param {Event} event - File input change event
     */
    handleFileSelect(event) {
      const files = event.target.files;
      this.addFiles(files);
      // Reset input to allow re-selecting same file
      event.target.value = '';
    },

    /**
     * Handle drag over event
     */
    handleDragOver() {
      if (!this.isLoading) {
        this.isDragOver = true;
      }
    },

    /**
     * Handle drag leave event
     */
    handleDragLeave() {
      this.isDragOver = false;
    },

    /**
     * Handle file drop event
     * @param {DragEvent} event - Drop event
     */
    handleDrop(event) {
      this.isDragOver = false;
      if (this.isLoading) return;
      
      const files = event.dataTransfer.files;
      this.addFiles(files);
    },

    /**
     * Add files to selected files list with validation
     * @param {FileList|Array<File>} files - Files to add
     */
    addFiles(files) {
      const fileArray = Array.from(files);
      
      // Add all selected files (validation will show error in UI)
      this.selectedFiles = [...this.selectedFiles, ...fileArray];
      
      // Calculate total size
      const totalSize = this.selectedFiles.reduce((sum, file) => sum + file.size, 0);
      
      // Show warning toasts for exceeded limits
      if (this.selectedFiles.length > this.maxFiles) {
        this.$store.commit('sbError', `Maximum ${this.maxFiles} files allowed. Please remove ${this.selectedFiles.length - this.maxFiles} file(s) to proceed.`);
      } else if (totalSize > this.maxTotalSize) {
        this.$store.commit('sbError', `Total size exceeds ${this.formatFileSize(this.maxTotalSize)} limit. Please remove some files.`);
      }
      
      console.log('[Evidence] Files selected:', this.selectedFiles.length);
    },

    /**
     * Remove a file from selected files list
     * @param {number} index - Index of file to remove
     */
    removeFile(index) {
      if (index >= 0 && index < this.selectedFiles.length) {
        this.selectedFiles.splice(index, 1);
      }
    },

    /**
     * Clear all selected files
     */
    clearAllFiles() {
      this.selectedFiles = [];
    },

    /**
     * Format file size to human readable string
     * @param {number} bytes - File size in bytes
     * @returns {string} Formatted file size
     */
    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    /**
     * Get icon for file based on extension
     * @param {string} fileName - File name
     * @returns {string} MDI icon name
     */
    getFileIcon(fileName) {
      const ext = fileName.split('.').pop().toLowerCase();
      return FILE_ICON_MAP[ext] || 'mdi-file';
    },

    /**
     * Get chip color for file based on extension
     * @param {File} file - File object
     * @returns {string} Vuetify color
     */
    getFileChipColor(file) {
      const ext = file.name.split('.').pop().toLowerCase();
      return FILE_COLOR_MAP[ext] || 'grey darken-1';
    },

    /**
     * Get chip color for uploaded file based on filename
     * @param {string} fileName - File name string
     * @returns {string} Vuetify color
     */
    getUploadedFileChipColor(fileName) {
      if (!fileName) return 'success';
      const ext = fileName.split('.').pop().toLowerCase();
      return FILE_COLOR_MAP[ext] || 'success';
    },

    /**
     * Truncate long file names for display
     * @param {string} name - File name
     * @param {number} maxLength - Maximum length
     * @returns {string} Truncated name
     */
    truncateFileName(name, maxLength = 20) {
      if (name.length <= maxLength) return name;
      const ext = name.split('.').pop();
      const baseName = name.slice(0, name.length - ext.length - 1);
      const truncatedBase = baseName.slice(0, maxLength - ext.length - 4) + '...';
      return `${truncatedBase}.${ext}`;
    },

    /**
     * Get file extension from filename
     * @param {string} fileName - File name
     * @returns {string} File extension (uppercase)
     */
    getFileExtension(fileName) {
      if (!fileName) return '';
      return fileName.split('.').pop().toUpperCase();
    },

    /**
     * Format upload date for display
     * @param {string} dateString - ISO date string
     * @returns {string} Formatted date
     */
    formatUploadDate(dateString) {
      if (!dateString) return '-';
      try {
        const date = new Date(dateString);
        return date.toLocaleDateString('en-US', {
          month: 'short',
          day: 'numeric',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
        });
      } catch {
        return '-';
      }
    },

    /**
     * Get user info from upload detail's s3data object
     * @param {Object} fileDetail - Upload detail object
     * @returns {Object|null} User info object or null
     */
    getUploadUserInfo(fileDetail) {
      return fileDetail?.s3data?.userInfo || null;
    },

    /**
     * Format user role for display (capitalize first letter)
     * @param {string} role - Role string
     * @returns {string} Formatted role
     */
    formatUserRole(role) {
      if (!role) return '';
      // Capitalize each word
      return role
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
        .join(' ');
    },

    /**
     * Get CSS class for file icon wrapper based on file extension
     * @param {string} fileName - File name
     * @returns {string} CSS class name
     */
    getFileIconClass(fileName) {
      if (!fileName) return 'evidence__file-icon-wrapper--default';
      const ext = fileName.split('.').pop().toLowerCase();
      const classMap = {
        xlsx: 'evidence__file-icon-wrapper--excel',
        xls: 'evidence__file-icon-wrapper--excel',
        xlsb: 'evidence__file-icon-wrapper--excel',
        csv: 'evidence__file-icon-wrapper--csv',
        pdf: 'evidence__file-icon-wrapper--pdf',
        doc: 'evidence__file-icon-wrapper--word',
        docx: 'evidence__file-icon-wrapper--word',
        jpg: 'evidence__file-icon-wrapper--image',
        jpeg: 'evidence__file-icon-wrapper--image',
        png: 'evidence__file-icon-wrapper--image',
        ppt: 'evidence__file-icon-wrapper--ppt',
        pptx: 'evidence__file-icon-wrapper--ppt',
      };
      return classMap[ext] || 'evidence__file-icon-wrapper--default';
    },

    /**
     * Get CSS class for file type badge based on file extension
     * @param {string} fileName - File name
     * @returns {string} CSS class name
     */
    getFileTypeClass(fileName) {
      if (!fileName) return 'evidence__type-badge--default';
      const ext = fileName.split('.').pop().toLowerCase();
      const classMap = {
        xlsx: 'evidence__type-badge--excel',
        xls: 'evidence__type-badge--excel',
        xlsb: 'evidence__type-badge--excel',
        csv: 'evidence__type-badge--csv',
        pdf: 'evidence__type-badge--pdf',
        doc: 'evidence__type-badge--word',
        docx: 'evidence__type-badge--word',
        jpg: 'evidence__type-badge--image',
        jpeg: 'evidence__type-badge--image',
        png: 'evidence__type-badge--image',
        ppt: 'evidence__type-badge--ppt',
        pptx: 'evidence__type-badge--ppt',
      };
      return classMap[ext] || 'evidence__type-badge--default';
    },

    /**
     * Upload selected files to server
     * Emits 'upload-success' event with uploaded file IDs on success
     */
    async uploadFiles() {
      if (this.isLoading) return;
      
      if (this.selectedFiles.length === 0) {
        this.$store.commit('sbError', 'No files selected for upload');
        return;
      }

      if (this.totalSelectedSize > this.maxTotalSize) {
        this.$store.commit('sbError', `Total file size exceeds ${this.formatFileSize(this.maxTotalSize)} limit`);
        return;
      }

      this.isLoading = true;
      this.uploadPercentage = 0;

      try {
        const axiosInstance = this.axios || this.$axios || this.$http;
        
        // Upload files using global utility
        const uploadResponse = await this.$nova.uploadMultipleFiles(
          axiosInstance,
          this.selectedFiles,
          {
            maxFiles: this.maxFiles,
            maxTotalSize: this.maxTotalSize,
            progressfunction: (e) => {
              const p = (e.loaded * 100) / e.total;
              this.uploadPercentage = Math.round(p);
            },
          }
        );

        if (uploadResponse.status !== 200) {
          throw new Error('Unable to upload files');
        }

        // Extract upload IDs from response
        // API returns different formats:
        // Single file: { status: 'success', data: [{ _id: '...' }] }
        // Multiple files: [{ status: 'success', data: [{ _id: '...' }] }, ...]
        const responseData = uploadResponse.data;
        
        // Normalize response to always be an array
        const responseArray = Array.isArray(responseData) ? responseData : [responseData];
        const uploadIds = [];
        
        for (const response of responseArray) {
          if (response.status === 'success' && Array.isArray(response.data)) {
            for (const fileData of response.data) {
              if (fileData._id) {
                uploadIds.push(fileData._id);
              }
            }
          }
        }

        if (uploadIds.length === 0) {
          throw new Error('No files were uploaded successfully');
        }

        // Build filter for attach API
        const attachFilter = { cycletype: this.cycletype };
        if (this.reportingType) {
          attachFilter.reporting_type = this.reportingType;
        }

        // Build user info for upload tracking
        const userInfo = {};
        if (this.userId) {
          userInfo.userid = this.userId;
        }
        if (this.userRole) {
          userInfo.role = this.userRole;
        }
        // Prefer name, fallback to email
        if (this.userName) {
          userInfo.name = this.userName;
        } else if (this.userEmail) {
          userInfo.email = this.userEmail;
        }

        // Attach files to commentary
        const attachResponse = await axiosInstance.post(
          `/v3/varianceanalysis/analysis/attachfiles/${this.commentaryId}`,
          {
            data: { 
              uploads: uploadIds,
              ...userInfo,
            },
            filter: attachFilter,
          }
        );

        if (attachResponse.data.status !== 'success') {
          throw new Error(attachResponse.data.message || 'Error storing files');
        }

        // Emit success event with uploaded file IDs
        this.$emit('upload-success', uploadIds);
        this.$store.commit('sbSuccess', `${uploadIds.length} file(s) uploaded successfully`);
        
        // Clear selected files
        this.selectedFiles = [];
        
      } catch (err) {
        console.error('[Evidence] Upload error:', err);
        this.$store.commit('sbError', err.message || 'Unknown error occurred');
      } finally {
        this.isLoading = false;
        this.uploadPercentage = 0;
      }
    },

    /**
     * Download a file by upload ID
     * Uses the variance analysis attachment download endpoint for proper authorization
     * @param {string} uploadId - File upload ID
     */
    downloadFile(uploadId) {
      if (this.isDownloadDisabled) return;
      const url = `/v3/varianceanalysis/analysis/downloadattachments/${this.commentaryId}/t/${this.cycletype}${this.reportingType ? `/u/${this.reportingType}` : ''}`;
      const params = `&uploadid=${uploadId}&timestamp=${new Date().getTime()}`;
      this.$nova.downloadFile(undefined, {}, url, params);
    },

    /**
     * Download all files as a zip
     * Uses 'all' as uploadid to trigger zip download
     */
    downloadAllFiles() {
      if (this.isDownloadDisabled) return;
      const url = `/v3/varianceanalysis/analysis/downloadattachments/${this.commentaryId}/t/${this.cycletype}${this.reportingType ? `/u/${this.reportingType}` : ''}`;
      const params = `&uploadid=all&timestamp=${new Date().getTime()}`;
      this.$nova.downloadFile(undefined, {}, url, params);
    },

    /**
     * Download legacy single upload file
     */
    downloadLegacyFile() {
      if (this.isDownloadDisabled) return;
      const url = `/v3/varianceanalysis/analysis/downloadattachments/${this.commentaryId}/t/${this.cycletype}`;
      const params = `&uploadid=${this.legacyUploadId}&timestamp=${new Date().getTime()}`;
      this.$nova.downloadFile(undefined, {}, url, params);
    },

    /**
     * Reset component state
     * Called from parent or on destroy
     */
    resetState() {
      this.selectedFiles = [];
      this.isDragOver = false;
      this.isLoading = false;
      this.uploadPercentage = 0;
    },
  },

  /**
   * Reset state on component destroy to prevent stale state
   */
  beforeDestroy() {
    this.resetState();
  },
};
</script>

<style scoped>
.evidence {
  width: 100%;
}

/* Dropzone styles */
.evidence__dropzone {
  border: 2px dashed var(--v-grey-lighten2, #e0e0e0);
  border-radius: 8px;
  padding: 32px 24px;
  background-color: var(--v-grey-lighten5, #fafafa);
  transition: all 0.2s ease;
  cursor: pointer;
}

.evidence__dropzone:hover {
  border-color: var(--v-grey-lighten1, #bdbdbd);
  background-color: var(--v-grey-lighten3, #eeeeee);
}

.evidence__dropzone--active {
  border-color: var(--v-primary-base, #1976d2);
  background-color: var(--v-primary-lighten5, #e3f2fd);
  border-style: solid;
}

.evidence__dropzone--disabled {
  opacity: 0.6;
  cursor: not-allowed;
  pointer-events: none;
}

/* File list styles */
.evidence__file-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.evidence__file-chip {
  padding: 8px 14px !important;
  height: auto !important;
  font-size: 14px !important;
}

.evidence__file-chip .v-chip__close {
  margin-left: 8px;
}

.evidence__file-name {
  max-width: 180px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-size: 14px;
}

.evidence__file-size-chip {
  font-size: 13px;
  font-weight: 500;
  opacity: 0.95;
}

.evidence__file-size {
  opacity: 0.8;
  font-size: 0.75rem;
}

/* Selected files section */
.evidence__selected-files {
  padding: 16px;
  background-color: var(--v-grey-lighten5, #fafafa);
  border-radius: 8px;
  border: 1px solid var(--v-grey-lighten3, #eeeeee);
}

/* Uploaded files section */
.evidence__uploaded-files {
  padding: 16px;
  background-color: var(--v-grey-lighten5, #fafafa);
  border-radius: 8px;
  border: 1px solid var(--v-grey-lighten3, #eeeeee);
}

/* Table styling - matches main-table-style */
.evidence__table {
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--v-grey-lighten3, #eeeeee);
}

.evidence__table ::v-deep thead tr th {
  font-weight: 600;
  font-size: 13px;
  color: #2c2c35 !important;
  background-color: var(--color-table-header-bg, #f5f5f5);
  white-space: nowrap;
  padding: 10px 12px !important;
  border-bottom: 1px solid var(--v-grey-lighten2, #e0e0e0);
}

.evidence__table ::v-deep tbody tr {
  min-height: 42px;
}

.evidence__table ::v-deep tbody tr:hover {
  background-color: var(--v-grey-lighten5, #fafafa);
}

.evidence__table ::v-deep tbody tr td {
  min-height: 42px;
  padding: 8px 12px !important;
  font-weight: 400;
  font-size: 13px;
  color: #1e293b;
  border-bottom: 1px solid var(--v-grey-lighten3, #eeeeee);
}

.evidence__table ::v-deep tbody tr:last-child td {
  border-bottom: none;
}

.evidence__table-filename {
  max-width: 240px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  display: inline-block;
  font-size: 14px;
  color: var(--grey-900, #2C2C35);
}

/* File icon wrapper with color backgrounds */
.evidence__file-icon-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 6px;
  margin-right: 10px;
  flex-shrink: 0;
}

.evidence__file-icon-wrapper--excel {
  background: linear-gradient(135deg, #22c55e 0%, #16a34a 100%);
}

.evidence__file-icon-wrapper--csv {
  background: linear-gradient(135deg, #14b8a6 0%, #0d9488 100%);
}

.evidence__file-icon-wrapper--pdf {
  background: linear-gradient(135deg, #ef4444 0%, #dc2626 100%);
}

.evidence__file-icon-wrapper--word {
  background: linear-gradient(135deg, #3b82f6 0%, #2563eb 100%);
}

.evidence__file-icon-wrapper--image {
  background: linear-gradient(135deg, #a855f7 0%, #9333ea 100%);
}

.evidence__file-icon-wrapper--ppt {
  background: linear-gradient(135deg, #f97316 0%, #ea580c 100%);
}

.evidence__file-icon-wrapper--default {
  background: linear-gradient(135deg, #6b7280 0%, #4b5563 100%);
}

/* File type badge styling */
.evidence__type-badge {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.evidence__type-badge--excel {
  background-color: #dcfce7;
  color: #16a34a;
}

.evidence__type-badge--csv {
  background-color: #ccfbf1;
  color: #0d9488;
}

.evidence__type-badge--pdf {
  background-color: #fee2e2;
  color: #dc2626;
}

.evidence__type-badge--word {
  background-color: #dbeafe;
  color: #2563eb;
}

.evidence__type-badge--image {
  background-color: #f3e8ff;
  color: #9333ea;
}

.evidence__type-badge--ppt {
  background-color: #ffedd5;
  color: #ea580c;
}

.evidence__type-badge--default {
  background-color: #f3f4f6;
  color: #4b5563;
}

/* File size styling */
.evidence__file-size {
  font-size: 12px;
  color: #64748b;
  font-weight: 500;
}

/* Upload date styling */
.evidence__upload-date {
  font-size: 12px;
  color: #64748b;
}

/* Created By column styling - Modern card-like design */
.evidence__created-by {
  display: flex;
  align-items: center;
  gap: 10px;
}

.evidence__created-by-avatar {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--primary-100, #CCD6DD) 0%, var(--primary-200, #99ADBB) 100%);
  flex-shrink: 0;
}

.evidence__avatar-icon {
  width: 18px;
  height: 18px;
  opacity: 0.8;
}

.evidence__created-by-info {
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
}

.evidence__created-by-name {
  font-weight: 600;
  font-size: 14px;
  color: var(--primary-500, #193541);
  line-height: 1.3;
}

.evidence__created-by-role {
  font-size: 12px;
  color: var(--color-secondary, #274856);
  line-height: 1.2;
  text-transform: capitalize;
}

/* Empty state */
.evidence__empty {
  padding: 24px;
  background-color: var(--v-grey-lighten5, #fafafa);
  border-radius: 8px;
}

/* Constraints chip styling - matches StatusIndicator outlined style */
.evidence__constraints-chip {
  display: inline-flex;
  align-items: center;
  border: 1px solid #9e9e9e;
  border-radius: 8px;
  padding: 4px 10px;
  font-size: 0.75rem;
  color: #616161;
  background-color: transparent;
  width: fit-content;
  transition: all 0.2s ease;
}

.evidence__constraints-chip--warning {
  border-color: var(--amber-default, #EBA937);
  background-color: rgba(235, 169, 55, 0.1);
  color: #b47a00;
}
</style>
